import { Box, Divider, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import { Badge, Banner, Button, Modal } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHand } from '@fortawesome/pro-solid-svg-icons';
import { IAffiliationDetails, IJournalWiseUserRoleList } from '@publishing-engine/domain/models';
import { MergeCountLabels } from './MergeAccountsPanel';
import UserItem from '../../SubmissionContexts/Users/UserItem';
import UserTagBadgeList, { MergeUserTagSummary } from './UserTagBadgeList';

interface MergeProfileSummary {
  fullName: string;
  orcid: string;
  email: string;
  role: string;
  roles: string[];
  journals: IJournalWiseUserRoleList[];
  tags: MergeUserTagSummary[];
  affiliation: string;
  affiliationDetails?: IAffiliationDetails;
  submissions: string;
  draftSubmissions: string;
  archiveSubmissions: string;
  inProgressReviews: string;
  completedReviews: string;
  invitedReviews: string;
  totalReviews: string;
}

interface MergeAccountSummary {
  userId: string;
  name: string;
  email: string;
}

interface ConfirmMergeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isConfirming?: boolean;
  mergedProfile: MergeProfileSummary;
  primaryAccount: MergeAccountSummary;
  accountsToDeactivate: MergeAccountSummary[];
  countLabels: MergeCountLabels;
}

const ConfirmMergeModal = ({
  isOpen,
  onClose,
  onConfirm,
  isConfirming = false,
  mergedProfile,
  primaryAccount,
  accountsToDeactivate,
  countLabels
}: ConfirmMergeModalProps) => {
  const labelMinWidth = '170px';
  const modalWidth = useBreakpointValue({
    base: 'calc(100vw - 32px)',
    md: '760px'
  });
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width={modalWidth}
      closeButtonProps={{
        hidden: true
      }}
    >
      <Flex
        direction="column"
        backgroundColor="surfacePrimaryFill"
        gap={6}
        padding={{ base: 4, md: 8 }}
      >
        <Text textStyle="headingGroup" tabIndex={1}>
          Confirm Merge
        </Text>

        <Banner
          variant="attention"
          customIcon={<FontAwesomeIcon icon={faHand} />}
          heading="This action cannot be undone"
          content="Merging these accounts is permanent. Secondary accounts will be deactivated and all data consolidated into the primary account. Proceed only if you are certain."
        />

        <Flex direction="column" gap={6}>
          <Flex gap={3} alignItems="flex-start">
            <Flex direction="column" gap={4} width="100%">
              <Text textStyle="headingItem">Merged profile will use:</Text>

              <Flex direction="column" gap={3}>
                <Flex
                  gap={{ base: 1, md: 6 }}
                  direction={{ base: 'column', md: 'row' }}
                >
                  <Text
                    textStyle="textBody"
                    minWidth={{ base: 'auto', md: labelMinWidth }}
                  >
                    Full Name
                  </Text>
                  <Text textStyle="textBody">{mergedProfile.fullName}</Text>
                </Flex>
                <Flex
                  gap={{ base: 1, md: 6 }}
                  direction={{ base: 'column', md: 'row' }}
                >
                  <Text
                    textStyle="textBody"
                    minWidth={{ base: 'auto', md: labelMinWidth }}
                  >
                    ORCiD
                  </Text>
                  <Text textStyle="textBody">{mergedProfile.orcid}</Text>
                </Flex>
                <Flex
                  gap={{ base: 1, md: 6 }}
                  direction={{ base: 'column', md: 'row' }}
                >
                  <Text
                    textStyle="textBody"
                    minWidth={{ base: 'auto', md: labelMinWidth }}
                  >
                    Email
                  </Text>
                  <Text textStyle="textBody">{mergedProfile.email}</Text>
                </Flex>
                <Flex
                  gap={{ base: 1, md: 6 }}
                  alignItems="flex-start"
                  direction={{ base: 'column', md: 'row' }}
                >
                  <Text
                    textStyle="textBody"
                    minWidth={{ base: 'auto', md: labelMinWidth }}
                  >
                    Journal & Roles
                  </Text>
                  <Box flex="1" minWidth={0}>
                    <UserItem data={mergedProfile.journals} />
                  </Box>
                </Flex>
                <Flex
                  gap={{ base: 1, md: 6 }}
                  alignItems="flex-start"
                  direction={{ base: 'column', md: 'row' }}
                >
                  <Text
                    textStyle="textBody"
                    minWidth={{ base: 'auto', md: labelMinWidth }}
                  >
                    Tags
                  </Text>
                  <Box flex="1" minWidth={0}>
                    <UserTagBadgeList
                      tags={mergedProfile.tags}
                      keyPrefix="confirm-merge-tags"
                      maxVisible={5}
                    />
                  </Box>
                </Flex>
                <Flex
                  gap={{ base: 1, md: 6 }}
                  alignItems="flex-start"
                  direction={{ base: 'column', md: 'row' }}
                >
                  <Text
                    textStyle="textBody"
                    minWidth={{ base: 'auto', md: labelMinWidth }}
                  >
                    Affiliation
                  </Text>
                  {mergedProfile.affiliationDetails ? (
                    <Flex direction="column" gap={1}>
                      <Flex alignItems="center" gap={2}>
                        <Text textStyle="headingItem">
                          {mergedProfile.affiliationDetails.organizationName}
                        </Text>
                      </Flex>
                      {(mergedProfile.affiliationDetails.country ||
                        mergedProfile.affiliationDetails.countryCode) && (
                        <Text textStyle="textBody">
                          {mergedProfile.affiliationDetails.country}
                          {mergedProfile.affiliationDetails.countryCode &&
                            ` (${mergedProfile.affiliationDetails.countryCode})`}
                        </Text>
                      )}
                      {mergedProfile.affiliationDetails.streetAddress && (
                        <Text textStyle="textBody">
                          {mergedProfile.affiliationDetails.streetAddress}
                        </Text>
                      )}
                      {(mergedProfile.affiliationDetails.city ||
                        mergedProfile.affiliationDetails.postalCode) && (
                        <Text textStyle="textBody">
                          {[
                            mergedProfile.affiliationDetails.city,
                            mergedProfile.affiliationDetails.postalCode
                          ]
                            .filter(Boolean)
                            .join('- ')}
                        </Text>
                      )}
                    </Flex>
                  ) : (
                    <Text textStyle="textBody" whiteSpace="pre-wrap">
                      {mergedProfile.affiliation}
                    </Text>
                  )}
                </Flex>
                <Flex
                  gap={{ base: 1, md: 6 }}
                  alignItems="flex-start"
                  direction={{ base: 'column', md: 'row' }}
                >
                  <Text
                    textStyle="textBody"
                    minWidth={{ base: 'auto', md: labelMinWidth }}
                  >
                    Submissions
                  </Text>
                  <Flex gap={2} wrap="wrap">
                    {[
                      { label: countLabels.activeSubmissions, value: mergedProfile.submissions },
                      { label: countLabels.draftSubmissions, value: mergedProfile.draftSubmissions },
                      { label: countLabels.archiveSubmissions, value: mergedProfile.archiveSubmissions }
                    ].map(({ label, value }) => (
                      <Badge key={label} size="context" variant="neutral">
                        {label}: {value}
                      </Badge>
                    ))}
                  </Flex>
                </Flex>
                <Flex
                  gap={{ base: 1, md: 6 }}
                  alignItems="flex-start"
                  direction={{ base: 'column', md: 'row' }}
                >
                  <Text
                    textStyle="textBody"
                    minWidth={{ base: 'auto', md: labelMinWidth }}
                  >
                    Reviews
                  </Text>
                  <Flex gap={2} wrap="wrap">
                    {[
                      { label: countLabels.totalReviews, value: mergedProfile.totalReviews },
                      { label: countLabels.inProgressReviews, value: mergedProfile.inProgressReviews },
                      { label: countLabels.completedReviews, value: mergedProfile.completedReviews },
                      { label: countLabels.invitedReviews, value: mergedProfile.invitedReviews }
                    ].map(({ label, value }) => (
                      <Badge key={label} size="context" variant="neutral">
                        {label}: {value}
                      </Badge>
                    ))}
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Flex>

          <Divider borderColor="#D9D9D9" />

          <Flex direction="column" gap={4}>
            <Flex direction="column" gap={1}>
              <Text textStyle="headingItem">Primary Account:</Text>
              <Text textStyle="textBody">{primaryAccount.name}</Text>
              <Text textStyle="textBody">{primaryAccount.email}</Text>
            </Flex>

            <Flex direction="column" gap={2}>
              <Text textStyle="headingItem">Accounts to be deactivated:</Text>
              <Box
                display="grid"
                gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
                columnGap={8}
                rowGap={6}
              >
                {accountsToDeactivate.map((account, index) => (
                  <Box
                    key={account.userId}
                    minWidth={0}
                    paddingLeft={index % 2 === 1 ? { base: 0, md: 6 } : 0}
                    borderLeft={
                      index % 2 === 1
                        ? { base: 'none', md: '1px solid #D9D9D9' }
                        : 'none'
                    }
                  >
                    <Text textStyle="textBody">{account.name}</Text>
                    <Text textStyle="textBody">{account.email}</Text>
                  </Box>
                ))}
              </Box>
            </Flex>
          </Flex>
        </Flex>

        <Flex
          justifyContent="flex-end"
          direction={{ base: 'column-reverse', md: 'row' }}
          gap={3}
          width="100%"
        >
          <Button
            variant="secondary"
            onClick={onClose}
            isDisabled={isConfirming}
            width={{ base: '100%', md: 'auto' }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            isDisabled={isConfirming}
            isLoading={isConfirming}
            width={{ base: '100%', md: 'auto' }}
          >
            Confirm Merge
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default ConfirmMergeModal;
